import React, { useRef, useState, useEffect } from "react";
import { useReactToPrint } from "react-to-print";
import { Printer, X, MessageCircle, Download, ClipboardList } from "lucide-react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const InvoiceReceipt = ({ transaction, onClose, companyInfo, autoPrint = false }) => {
  const invoiceRef = useRef();
  const itemSlipRef = useRef();
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);



  // If amount is 0 and it involves shakes, it's a consumption from balance
  const isShakeConsumption = (transaction?.totalShakeQuantity > 0 && (transaction?.totalAmount || 0) === 0);

  // Shared page style injected into react-to-print's iframe
  const thermalPageStyle = `
    @page {
      size: 72mm auto;
      margin: 0mm;
    }
    * {
      box-sizing: border-box;
    }
    body {
      margin: 0;
      padding: 0;
      background: #fff;
      font-family: 'Inter', 'Roboto', 'Helvetica Neue', Arial, sans-serif;
      color: #000;
      width: 72mm;
      -webkit-font-smoothing: antialiased;
    }
  `;

  // Detect if this is a Trial order (case-insensitive)
  const isTrialOrder = transaction?.transactionType?.toLowerCase().includes("trial");
  const isComplementary = isTrialOrder;

  // Print the main invoice (1st print job → 1st cutter),
  // then auto-trigger item label print if needed
  const handlePrintInvoice = useReactToPrint({
    contentRef: invoiceRef,
    documentTitle: `Invoice_${transaction?.orderId || transaction?.billNo || "N/A"}`,
    pageStyle: thermalPageStyle,
  });

  // Print the item slip (2nd print job → kitchen/preparation staff)
  const handlePrintItemSlip = useReactToPrint({
    contentRef: itemSlipRef,
    documentTitle: `ItemSlip_${transaction?.orderId || transaction?.billNo || "N/A"}`,
    pageStyle: thermalPageStyle,
  });

  // Combined print handler: always auto-print item slip after invoice
  const handlePrintWithAutoSlip = () => {
    handlePrintInvoice();
    setTimeout(() => {
      handlePrintItemSlip();
    }, 800);
  };

  // Auto-print when the modal first mounts (if autoPrint prop is true)
  useEffect(() => {
    if (autoPrint) {
      const timer = setTimeout(() => {
        handlePrintWithAutoSlip();
      }, 400); // slight delay to let the DOM render fully
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPrint]);

  // Share invoice to WhatsApp
  const handleWhatsAppShare = async () => {
    let mobile = transaction?.userMobile?.replace(/\D/g, "") || "";
    if (!mobile) {
      alert("Customer mobile number not available");
      return;
    }
    if (mobile.length === 10) {
      mobile = "91" + mobile;
    }
    try {
      const element = invoiceRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        backgroundColor: "#ffffff",
        logging: false,
      });
      canvas.toBlob(async (blob) => {
        try {
          const item = new ClipboardItem({ "image/png": blob });
          await navigator.clipboard.write([item]);
          const whatsappUrl = `https://wa.me/${mobile}`;
          window.open(whatsappUrl, "_blank");
          // alert("✅ Invoice image copied to clipboard!\n\n📱 WhatsApp is opening in a new tab...\n\n✨ Simply press Ctrl+V (or Cmd+V on Mac) in the WhatsApp chat to paste and send the invoice!");
        } catch (clipboardError) {
          const customerName = (transaction?.userName || "Customer").replace(/\s+/g, "_");
          const date = new Date(transaction?.createdAt).toLocaleDateString("en-IN").replace(/\//g, "-");
          const filename = `Invoice_${customerName}_${mobile}_${date}.png`;
          const link = document.createElement("a");
          link.download = filename;
          link.href = canvas.toDataURL("image/png");
          link.click();
          const whatsappUrl = `https://wa.me/${mobile}`;
          window.open(whatsappUrl, "_blank");
        }
      }, "image/png");
    } catch (error) {
      console.error("Error sharing to WhatsApp:", error);
      alert("Failed to create invoice image. Please try again.");
    }
  };

  // Download invoice as image
  const handleDownloadImage = async () => {
    try {
      const element = invoiceRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        backgroundColor: "#ffffff",
        logging: false,
      });
      const customerName = (transaction?.userName || "Customer").replace(/\s+/g, "_");
      const mobile = transaction?.userMobile || "NoMobile";
      const date = new Date(transaction?.createdAt).toLocaleDateString("en-IN").replace(/\//g, "-");
      const filename = `Invoice_${customerName}_${mobile}_${date}.png`;
      const link = document.createElement("a");
      link.download = filename;
      link.href = canvas.toDataURL("image/png");
      link.click();
      setShowDownloadMenu(false);
    } catch (error) {
      console.error("Error downloading image:", error);
      alert("Failed to download image");
    }
  };

  // Download invoice as PDF
  const handleDownloadPDF = async () => {
    try {
      const element = invoiceRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        backgroundColor: "#ffffff",
        logging: false,
      });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "px",
        format: [canvas.width, canvas.height],
      });
      pdf.addImage(imgData, "PNG", 0, 0, canvas.width, canvas.height);
      const customerName = (transaction?.userName || "Customer").replace(/\s+/g, "_");
      const mobile = transaction?.userMobile || "NoMobile";
      const date = new Date(transaction?.createdAt).toLocaleDateString("en-IN").replace(/\//g, "-");
      const filename = `Invoice_${customerName}_${mobile}_${date}.pdf`;
      pdf.save(filename);
      setShowDownloadMenu(false);
    } catch (error) {
      console.error("Error downloading PDF:", error);
      alert("Failed to download PDF");
    }
  };

  // Default company info
  const defaultCompanyInfo = {
    name: "MP3H Nutrition Center",
    address1: "Subham Towers, 2nd Floors Five Road",
    address2: "Salem 636005",
    mobile: "7200570369, 9360792890",
    gstin: "33AMNPM1855G3ZX",
  };

  const company = companyInfo || defaultCompanyInfo;

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  // Shared receipt styles
  const receiptStyle = {
    width: "72mm",
    margin: "auto",
    padding: "4mm 2mm",
    fontFamily: "'Inter', 'Roboto', sans-serif",
    color: "#000",
    background: "#fff",
    lineHeight: "1.2",
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">

        {/* Modal Header */}
        <div className="bg-slate-900 px-4 py-3 rounded-t-2xl border-b border-slate-700 flex items-center justify-between">
          <h2 className="text-[13px] font-semibold text-white">
            Invoice Receipt
          </h2>
          <div className="flex gap-2">
            <button
              onClick={handleWhatsAppShare}
              className="text-white hover:bg-slate-800 rounded-lg p-2 transition-colors"
              title="Share via WhatsApp"
            >
              <MessageCircle className="w-4 h-4" />
            </button>
            <div className="relative">
              <button
                onClick={() => setShowDownloadMenu(!showDownloadMenu)}
                className="text-white hover:bg-slate-800 rounded-lg p-2 transition-colors"
                title="Download"
              >
                <Download className="w-4 h-4" />
              </button>
              {showDownloadMenu && (
                <div className="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-50">
                  <button onClick={handleDownloadImage} className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 transition-colors">
                    Download as Image
                  </button>
                  <button onClick={handleDownloadPDF} className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-100 transition-colors">
                    Download as PDF
                  </button>
                </div>
              )}
            </div>
            <button
              onClick={handlePrintWithAutoSlip}
              className="relative text-white hover:bg-slate-800 rounded-lg p-2 transition-colors"
              title="Print Invoice + Item Slip"
            >
              <Printer className="w-4 h-4" />
            </button>
            <button
              onClick={handlePrintItemSlip}
              className="text-white hover:bg-slate-800 rounded-lg p-2 transition-colors"
              title="Print Item Slip"
            >
              <ClipboardList className="w-4 h-4" />
            </button>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg p-2 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Receipt Preview */}
        <div className="p-6 max-h-[70vh] overflow-y-auto space-y-4">

          {/* ── PRINT 1: Full Invoice Receipt ── */}
          <div ref={invoiceRef} style={receiptStyle}>
            {/* Company Header */}
            <div className="text-center mb-4 border-b border-black pb-3">
              <h1 className="text-[16px] font-extrabold tracking-tight mb-1 uppercase">{company.name}</h1>
              <p className="text-[10px] leading-tight font-medium">{company.address1}</p>
              <p className="text-[10px] leading-tight font-medium">{company.address2}</p>
              <p className="text-[11px] mt-1 font-bold">MOB: {company.mobile}</p>
              <p className="text-[9px] mt-0.5 font-semibold text-slate-600">GSTIN: {company.gstin}</p>
            </div>

            {/* Bill Info */}
            <div className="flex justify-between text-[10px] mb-3 font-bold border-b border-black pb-2">
              <div>
                {/* <p className="text-[12px]">BILL NO: {transaction?.orderId || transaction?.billNo || transaction?.id?.slice(-6) || "N/A"}</p> */}
                {transaction?.dailySerial !== undefined && transaction?.dailySerial !== null && (
                  <p className="text-[12px] font-black text-black mt-0.5">SL NO: <span className="font-semibold">{transaction.dailySerial}</span></p>
                )}
              </div>
              <div className="text-right font-medium">
                <p>DATE: {formatDate(transaction?.createdAt)}</p>
                <p>TIME: {formatTime(transaction?.createdAt)}</p>

              </div>
            </div>

            {/* Customer Info */}
            <div className="text-[10px] mb-3 border-b border-black pb-2">
              <div className="flex justify-between items-baseline">
                <p className="font-bold text-[11px]">CUSTOMER: {transaction?.userName || "N/A"}</p>
              </div>
              <div className="flex justify-between mt-1 items-baseline">
                <p className="font-medium">MOBILE: {transaction?.userMobile || "N/A"}</p>
                <p className="font-extrabold text-[11px] px-1 rounded">TYPE: {transaction?.transactionType || "N/A"}</p>
              </div>
            </div>

            {/* Items Header */}
            <div className="border-b border-black pb-1 mb-2">
              <div className="flex justify-between text-[10px] font-extrabold">
                <span className="w-[45%]">ITEM DESCRIPTION</span>
                <span className="w-[10%] text-center">QTY</span>
                <span className="w-[20%] text-right">PRICE</span>
                <span className="w-[25%] text-right">AMOUNT</span>
              </div>
            </div>

            {/* Items List */}
            <div className="mb-3">
              {transaction?.products && transaction.products.length > 0 ? (
                transaction.products.map((product, index) => {
                  // The display price (PRICE column) shows the original value
                  const displayPrice = product.originalPrice || product.pricePerUnit || product.productPrice || product.price || product.mrp || 0;

                  // The actual price charged (used for AMOUNT column)
                  const actualPrice = product.pricePerUnit !== undefined ? product.pricePerUnit : (product.price || displayPrice);

                  const productQty = product.quantity || 0;
                  const isTRIALItem = product.discount === 100;
                  const productAmount = isTRIALItem ? 0 : (actualPrice * productQty);
                  const productName = product.productName || product.name || "N/A";
                  const specialTypeText = product.specialType ? product.specialType.toUpperCase() : "TRIAL";
                  const content = (
                    <>
                      <span className="w-[45%] break-words leading-tight font-semibold">
                        <span>{productName}</span>
                        {isTRIALItem && (
                          <span className="block text-[8px] font-bold text-gray-600 mt-0.5">
                            ({specialTypeText})
                          </span>
                        )}
                      </span>
                      <span className="w-[10%] text-center font-bold">{productQty}</span>
                      <span className="w-[20%] text-right font-bold">{isTrialOrder ? "₹0" : (isTRIALItem ? "₹0" : `₹${displayPrice.toFixed(0)}`)}</span>
                      <span className="w-[25%] text-right font-bold">{isTrialOrder ? "₹0" : (isTRIALItem ? "₹0" : `₹${productAmount.toFixed(0)}`)}</span>
                    </>
                  );

                  if (isTRIALItem) {
                    return (
                      <div
                        key={index}
                        className="mb-1.5 p-1 border-2 flex justify-between text-[10px] font-medium"
                        style={{
                          borderStyle: "solid",
                          borderImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10'%3E%3Ctext x='50%25' y='50%25' font-size='8' text-anchor='middle' dominant-baseline='middle' fill='black'%3E%E2%98%85%3C/text%3E%3C/svg%3E\") 33% repeat",
                          margin: "2px 0",
                        }}
                      >
                        {content}
                      </div>
                    );
                  }

                  return (
                    <div key={index} className="flex justify-between text-[10px] mb-1.5 font-medium border-b border-slate-50 pb-1">
                      {content}
                    </div>
                  );
                })
              ) : (
                <p className="text-[10px] text-center text-gray-500">No items</p>
              )}
            </div>

            {/* Totals — show ₹0 for Trial orders */}
            <div className="border-t border-black pt-2 mb-3">
              <div className="flex justify-between text-[11px] items-center">
                <div className="font-bold">
                  <span>ITEMS: {transaction?.products?.length || 0}</span>
                  <span className="ml-2">/ QTY: {transaction?.products?.reduce((sum, p) => sum + (p.quantity || 0), 0) || transaction?.totalShakeQuantity || 0}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-extrabold">TOTAL:</span>
                  <span className="font-extrabold text-[18px]">
                    {isTrialOrder ? "₹0" : `₹${(() => {
                      const calcTotal = transaction?.products?.reduce((sum, p) => {
                        const isComp = p.discount === 100;
                        if (isComp) return sum;
                        const actualPrice = p.pricePerUnit !== undefined ? p.pricePerUnit : (p.price || p.originalPrice || p.mrp || 0);
                        return sum + (actualPrice * (p.quantity || 0));
                      }, 0) || 0;
                      return calcTotal > 0 ? calcTotal.toFixed(0) : (transaction?.totalAmount || transaction?.total?.amount || 0).toFixed(0);
                    })()}`}
                  </span>
                </div>
              </div>
            </div>

            {/* Shake Balance */}
            {(transaction?.newShakeQuantity !== undefined || transaction?.previousShakeQuantity !== undefined) && (
              <div className="border-t border-dashed border-black pt-2 mb-3">
                <div className="text-[9px] space-y-1">
                  {transaction?.previousShakeQuantity !== undefined && transaction?.newShakeQuantity !== undefined && (
                    <>
                      <div className="flex justify-between items-center">
                        <span>PAYMENT:</span>
                        <span className="font-bold uppercase">{transaction.paymentMethod}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>PREV BAL:</span>
                        <span className="font-bold">{transaction.previousShakeQuantity} S</span>
                      </div>

                      {/* TopUp breakdown: consumed today + added */}
                      {transaction?.consumedOnTopUp > 0 ? (
                        <>
                          <div className="flex justify-between text-[9px]">
                            <span>CONSUMED TODAY:</span>
                            <span className="font-bold">−{transaction.consumedOnTopUp} Shake</span>
                          </div>
                          <div className="flex justify-between text-[9px]">
                            <span>TOPPED UP:</span>
                            <span className="font-bold">+{transaction.topUpAdded} Shake</span>
                          </div>
                          <div className="flex justify-between text-[9px]">
                            <span>NET CHANGE:</span>
                            <span className="font-bold">
                              {transaction.netShakeChange >= 0 ? '+' : ''}{transaction.netShakeChange} Shake
                            </span>
                          </div>
                        </>
                      ) : (
                        <div className="flex justify-between">
                          <span>USED:</span>
                          <span className="font-bold">
                            {transaction.previousShakeQuantity - transaction.newShakeQuantity > 0 ? '-' : '+'}
                            {Math.abs(transaction.previousShakeQuantity - transaction.newShakeQuantity)} S
                          </span>
                        </div>
                      )}
                    </>
                  )}
                  <div className="flex justify-between border-t border-black pt-1 mt-1">
                    <span className="font-bold">REMAINING:</span>
                    <span className="font-bold text-[11px]">
                      {transaction?.newShakeQuantity !== undefined ? `${transaction.newShakeQuantity} SHAKES` : 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* BODY PARAMETERS */}
            {transaction?.dailyRecord && (
              <div className="border-t border-black pt-2 mb-3">
                <p className="text-[10px] font-bold text-center mb-2 underline uppercase">BODY PARAMETERS</p>
                <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-[10px] font-bold">
                  <div className="flex justify-between"><span>WEIGHT:</span><span className="font-extrabold">{transaction.dailyRecord.weight || "0"} KG</span></div>
                  <div className="flex justify-between"><span>HEIGHT:</span><span className="font-extrabold">{transaction.dailyRecord.height || "0"} CM</span></div>
                  <div className="flex justify-between"><span>FAT:</span><span className="font-extrabold">{transaction.dailyRecord.fat || "0"}%</span></div>
                  <div className="flex justify-between"><span>VISCERAL FAT:</span><span className="font-extrabold">{transaction.dailyRecord.visceralFat || "0"}</span></div>
                  <div className="flex justify-between"><span>MUSCLE:</span><span className="font-extrabold">{transaction.dailyRecord.muscles || "0"}%</span></div>
                  <div className="flex justify-between"><span>BMR:</span><span className="font-extrabold">{transaction.dailyRecord.bmr || "0"}</span></div>
                  <div className="flex justify-between"><span>BODY AGE:</span><span className="font-extrabold">{transaction.dailyRecord.bodyAge || "0"}</span></div>
                  <div className="flex justify-between"><span>BMI:</span><span className="font-extrabold bg-slate-50 px-1">{transaction.dailyRecord.bmi || "0"}</span></div>
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="text-center border-t border-dashed border-black pt-3">
              <p className="text-[10px] font-bold tracking-tight uppercase">CARE MORE ABOUT OTHERS</p>
              <p className="text-[8px] mt-1">Healthy LifeStyle</p>
              <p className="text-[7px] mt-1 opacity-50">----------------------------</p>
            </div>
          </div>
          {/* end PRINT 1 */}

          {/* Screen-only divider */}
          <div className="text-center text-[11px] text-slate-400 font-semibold tracking-widest border-t border-dashed border-slate-300 pt-3 mt-1 print:hidden">
            ── ITEM SLIP (2nd Print) ──
          </div>

          {/* ── PRINT 2: Item Slip (Kitchen/Preparation) ── */}
          <div ref={itemSlipRef} style={receiptStyle}>

            {/* Item Slip Header — always show base shake type, strip TopUp prefix */}
            <div className="text-center mb-3 border-b border-black pb-2">
              <div style={{ padding: '2px 8px', marginTop: '4px', display: 'inline-block' }}>
                <p style={{ fontSize: '13px', fontWeight: '900', letterSpacing: '2px', textTransform: 'uppercase', margin: 0 }}>
                  {(transaction?.transactionType || "ORDER").replace(/^TopUp\s+/i, "")}
                </p>
              </div>
            </div>

            {/* Customer */}
            <div className="text-[10px] font-bold mb-3 border-b pb-2 flex justify-between items-center">
              <p>Name: <span className="text-lg">{transaction?.userName || "N/A"}</span></p>
              {transaction?.dailySerial !== undefined && transaction?.dailySerial !== null && (
                <p className="text-right"><span className="text-2xl font-black border rounded-full px-2 border-black">{transaction.dailySerial}</span></p>
              )}
            </div>

            {/* Items — for TopUp use itemSlipProducts (qty=1, today's shake) */}
            <div className="mb-3">
              {(() => {
                const slipProducts = transaction?.itemSlipProducts || transaction?.products || [];
                return slipProducts.length > 0 ? (
                  slipProducts.map((product, index) => {
                    const isTRIALItem = product.discount === 100;
                    const productName = product.productName || product.name || "N/A";
                    const specialTypeText = product.specialType ? product.specialType.toUpperCase() : "TRIAL";
                    const content = (
                      <span className="break-words leading-snug text-xl font-bold">
                        {productName}
                        {isTRIALItem && (
                          <span className="block text-sm font-bold text-gray-600 mt-0.5">
                            ({specialTypeText})
                          </span>
                        )}
                      </span>
                    );

                    if (isTRIALItem) {
                      return (
                        <div
                          key={index}
                          className="mb-2 p-1.5 border-2"
                          style={{
                            borderStyle: "solid",
                            borderImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10'%3E%3Ctext x='50%25' y='50%25' font-size='8' text-anchor='middle' dominant-baseline='middle' fill='black'%3E%E2%98%85%3C/text%3E%3C/svg%3E\") 33% repeat",
                            margin: "2px 0",
                          }}
                        >
                          {content}
                        </div>
                      );
                    }

                    return (
                      <div key={index} className="text-[11px] font-bold mb-2 border-b border-slate-100 pb-1">
                        {content}
                      </div>
                    );
                  })
                ) : (
                  <p className="text-[10px] text-center text-gray-500">No items</p>
                );
              })()}
            </div>
          </div>
          {/* end PRINT 2 */}

        </div>
      </div>

      {/* No @media print needed here — react-to-print uses its own iframe with pageStyle */}
    </div>
  );
};

export default InvoiceReceipt;
